import { useState, useRef } from "react";
import { submitLead } from "../../lib/api";
import { Loader2, Check, ChevronRight, ChevronLeft, Shield } from "lucide-react";
import {
  trackContactFormStarted, trackContactFormSubmitted, trackClickToCall,
} from "../../lib/analytics";

const ACCIDENT_OPTIONS = [
  "Car Accident", "Truck Accident", "Motorcycle Accident", "Pedestrian Accident",
  "Uber/Lyft Accident", "Drunk Driving Accident", "Hit and Run", "Workplace Injury",
  "Slip and Fall", "Wrongful Death", "Dog Bite", "Other",
];

export default function LeadForm({ compact = false, sourcePage = "" }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const formId = sourcePage ? `lead_form_${sourcePage}` : "lead_form";
  const startedRef = useRef(false);
  const [data, setData] = useState({
    accident_type: "",
    accident_date: "",
    has_attorney: false,
    injuries: "",
    city: "",
    full_name: "",
    phone: "",
    email: "",
    description: "",
  });

  const update = (k, v) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackContactFormStarted(formId);
    }
    setData((d) => ({ ...d, [k]: v }));
  };
  const totalSteps = compact ? 2 : 3;

  const next = () => {
    setError("");
    if (step === 1 && !data.accident_type) {
      setError("Please select your accident type.");
      return;
    }
    setStep((s) => Math.min(s + 1, totalSteps));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!data.full_name.trim() || !data.phone.trim()) {
      setError("Name and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitLead({ ...data, source_page: sourcePage || window.location.pathname });
      trackContactFormSubmitted(formId, { accident_type: data.accident_type });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl border border-black/5 p-8 md:p-10 shadow-sm text-center" data-testid="lead-form-success">
        <div className="w-14 h-14 mx-auto rounded-full bg-forest/10 grid place-items-center text-forest mb-4">
          <Check className="w-7 h-7" />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold text-forest mb-3">You're in good hands.</h3>
        <p className="text-charcoal-soft text-base md:text-lg mb-6 max-w-md mx-auto">
          We received your information. An Ohio attorney will reach out as soon as possible.
          If your situation is urgent, call us directly.
        </p>
        <a
          href="tel:12162455967"
          className="btn-primary"
          data-testid="success-call-btn"
          onClick={() => trackClickToCall("12162455967", "success_screen")}
        >
          Call (216) 245-5967 now
        </a>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 md:p-8"
      data-testid="lead-form"
    >
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${i < step ? "bg-terracotta" : "bg-forest/10"}`}
          />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-5 animate-fade-in">
          <div>
            <div className="overline mb-2">Step 1 of {totalSteps}</div>
            <h3 className="text-2xl md:text-3xl font-bold text-forest tracking-tight">
              What kind of accident?
            </h3>
            <p className="text-charcoal-soft mt-2">Select the option that best describes what happened.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {ACCIDENT_OPTIONS.map((a) => (
              <button
                type="button"
                key={a}
                onClick={() => update("accident_type", a)}
                className={`text-left text-sm py-3 px-4 rounded-lg border transition-all ${
                  data.accident_type === a
                    ? "border-terracotta bg-terracotta/5 text-terracotta font-bold"
                    : "border-black/10 hover:border-forest/40 text-charcoal"
                }`}
                data-testid={`accident-option-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {a}
              </button>
            ))}
          </div>
          {error && <p className="text-red-600 text-sm" data-testid="form-error">{error}</p>}
          <div className="flex justify-end pt-2">
            <button type="button" onClick={next} className="btn-primary" data-testid="lead-form-next-1">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {step === 2 && !compact && (
        <div className="space-y-5 animate-fade-in">
          <div>
            <div className="overline mb-2">Step 2 of {totalSteps}</div>
            <h3 className="text-2xl md:text-3xl font-bold text-forest tracking-tight">
              A few quick details
            </h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-charcoal mb-1 block">When did it happen?</label>
              <input
                type="date"
                value={data.accident_date}
                onChange={(e) => update("accident_date", e.target.value)}
                className="w-full p-3 rounded-lg border border-black/10 focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none"
                data-testid="lead-form-date"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-charcoal mb-1 block">City in Ohio</label>
              <input
                type="text"
                value={data.city}
                onChange={(e) => update("city", e.target.value)}
                placeholder="e.g., Cleveland"
                className="w-full p-3 rounded-lg border border-black/10 focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none"
                data-testid="lead-form-city"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-charcoal mb-1 block">Were you injured?</label>
              <select
                value={data.injuries}
                onChange={(e) => update("injuries", e.target.value)}
                className="w-full p-3 rounded-lg border border-black/10 focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none bg-white"
                data-testid="lead-form-injuries"
              >
                <option value="">Select an option</option>
                <option value="minor">Minor injuries</option>
                <option value="moderate">Moderate injuries</option>
                <option value="severe">Severe / hospitalization</option>
                <option value="none">No injuries</option>
                <option value="not_sure">Not sure yet</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-charcoal-soft" data-testid="lead-form-attorney-label">
              <input
                type="checkbox"
                checked={data.has_attorney}
                onChange={(e) => update("has_attorney", e.target.checked)}
                className="w-4 h-4 accent-forest"
                data-testid="lead-form-has-attorney"
              />
              I already have an attorney for this matter.
            </label>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex justify-between pt-2">
            <button type="button" onClick={prev} className="btn-ghost" data-testid="lead-form-back-2">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <button type="button" onClick={next} className="btn-primary" data-testid="lead-form-next-2">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {((step === 2 && compact) || step === 3) && (
        <div className="space-y-5 animate-fade-in">
          <div>
            <div className="overline mb-2">Final step</div>
            <h3 className="text-2xl md:text-3xl font-bold text-forest tracking-tight">
              How can we reach you?
            </h3>
            <p className="text-charcoal-soft mt-2">An Ohio attorney will call you within minutes. 100% free, no obligation.</p>
          </div>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="Full name"
              value={data.full_name}
              onChange={(e) => update("full_name", e.target.value)}
              className="w-full p-3 rounded-lg border border-black/10 focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none"
              required
              data-testid="lead-form-name"
            />
            <input
              type="tel"
              placeholder="Phone number"
              value={data.phone}
              onChange={(e) => update("phone", e.target.value)}
              className="w-full p-3 rounded-lg border border-black/10 focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none"
              required
              data-testid="lead-form-phone"
            />
            <input
              type="email"
              placeholder="Email (optional)"
              value={data.email}
              onChange={(e) => update("email", e.target.value)}
              className="w-full p-3 rounded-lg border border-black/10 focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none"
              data-testid="lead-form-email"
            />
            <textarea
              placeholder="Briefly tell us what happened (optional)"
              value={data.description}
              onChange={(e) => update("description", e.target.value)}
              rows={3}
              className="w-full p-3 rounded-lg border border-black/10 focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none resize-none"
              data-testid="lead-form-description"
            />
          </div>

          {error && <p className="text-red-600 text-sm" data-testid="form-error">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full btn-primary text-base py-4 disabled:opacity-50"
            data-testid="lead-form-submit"
          >
            {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>) : "Get my free case review"}
          </button>

          <div className="flex items-center justify-center gap-2 text-xs text-charcoal-soft pt-1">
            <Shield className="w-3.5 h-3.5" />
            100% Confidential · No Fees Unless You Win
          </div>

          {!compact && (
            <button type="button" onClick={prev} className="btn-ghost w-full" data-testid="lead-form-back-final">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          )}
        </div>
      )}
    </form>
  );
}
